import {
  Card,
  Radio,
  Input,
  InputNumber,
  Switch,
  Button,
  Divider,
  Collapse,
} from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import { useNotificationSettings } from '@/hooks/useNotificationSettings'
import { displayAmountToQuota, quotaToDisplayAmount } from '@/utils/quota'
import type { NotifyType } from '@/api/user'

interface NotificationSettingsCardProps {
  rawSetting?: string
  onSaved: () => Promise<void>
}

export default function NotificationSettingsCard({ rawSetting, onSaved }: NotificationSettingsCardProps) {
  const { t } = useTranslation()
  const { values, patch, save, saving } = useNotificationSettings(rawSetting, onSaved)
  const thresholdUsd = quotaToDisplayAmount(values.quota_warning_threshold)

  return (
    <Card className="tt-card !rounded-2xl !border-[var(--console-border)]" title={t('personal.notify.title')}>
      <div className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-2">{t('personal.notify.method')}</div>
          <Radio.Group
            value={values.notify_type}
            onChange={(v) => patch({ notify_type: v as NotifyType })}
          >
            <Radio value="email">{t('personal.notify.email')}</Radio>
            <Radio value="webhook">{t('personal.notify.webhook')}</Radio>
            <Radio value="bark">{t('personal.notify.bark')}</Radio>
            <Radio value="gotify">{t('personal.notify.gotify')}</Radio>
          </Radio.Group>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t('personal.notify.threshold')}</div>
          <p className="text-xs text-[var(--console-text-muted)] mb-2">{t('personal.notify.thresholdHint')}</p>
          <InputNumber
            className="w-full max-w-xs"
            min={0.01}
            step={0.01}
            precision={2}
            prefix="$"
            value={thresholdUsd}
            onChange={(v) => patch({ quota_warning_threshold: displayAmountToQuota(Number(v) || 0) })}
          />
        </div>

        <Collapse bordered={false} defaultActiveKey={['channels']}>
          <Collapse.Item header={t('personal.notify.channelConfig')} name="channels">
            {values.notify_type === 'email' && (
              <Input
                className="mt-2"
                placeholder={t('personal.notify.notificationEmail')}
                value={values.notification_email}
                onChange={(v) => patch({ notification_email: v })}
              />
            )}
            {values.notify_type === 'webhook' && (
              <div className="space-y-2 mt-2">
                <Input
                  placeholder={t('personal.notify.webhookUrl')}
                  value={values.webhook_url}
                  onChange={(v) => patch({ webhook_url: v })}
                />
                <Input.Password
                  placeholder={t('personal.notify.webhookSecret')}
                  value={values.webhook_secret}
                  onChange={(v) => patch({ webhook_secret: v })}
                />
              </div>
            )}
            {values.notify_type === 'bark' && (
              <Input
                className="mt-2"
                placeholder={t('personal.notify.barkUrl')}
                value={values.bark_url}
                onChange={(v) => patch({ bark_url: v })}
              />
            )}
            {values.notify_type === 'gotify' && (
              <div className="space-y-2 mt-2">
                <Input
                  placeholder={t('personal.notify.gotifyUrl')}
                  value={values.gotify_url}
                  onChange={(v) => patch({ gotify_url: v })}
                />
                <Input.Password
                  placeholder={t('personal.notify.gotifyToken')}
                  value={values.gotify_token}
                  onChange={(v) => patch({ gotify_token: v })}
                />
                <InputNumber
                  className="w-full"
                  min={0}
                  max={10}
                  placeholder={t('personal.notify.gotifyPriority')}
                  value={values.gotify_priority}
                  onChange={(v) => patch({ gotify_priority: Number(v) })}
                />
              </div>
            )}
          </Collapse.Item>
        </Collapse>

        <Divider />

        <div>
          <div className="text-sm font-medium mb-1">{t('personal.notify.auditSection')}</div>
          <p className="text-xs text-[var(--console-text-muted)] mb-2">{t('personal.notify.recordIpLogHint')}</p>
          <Switch
            checked={values.record_ip_log}
            onChange={(checked) => patch({ record_ip_log: checked })}
          >
            {t('personal.notify.recordIpLog')}
          </Switch>
        </div>

        <Button type="primary" loading={saving} onClick={() => void save()}>
          {t('personal.notify.save')}
        </Button>
      </div>
    </Card>
  )
}
